import type { ProjectDetailsResponse } from "@/types/project";

export function projectDetailsTimestamp(details: ProjectDetailsResponse | null | undefined): number | null {
  const timestamp = details?.project?.timestamp;
  return typeof timestamp === "number" && Number.isFinite(timestamp) ? timestamp : null;
}

/** Prefer the payload with the greater `project.timestamp` for the same project id. */
export function pickNewerProjectDetails(
  left: ProjectDetailsResponse | null | undefined,
  right: ProjectDetailsResponse | null | undefined
): ProjectDetailsResponse | null {
  if (!left?.project_id) {
    return right ?? null;
  }

  if (!right?.project_id) {
    return left;
  }

  if (left.project_id !== right.project_id) {
    return left;
  }

  const leftTimestamp = projectDetailsTimestamp(left);
  const rightTimestamp = projectDetailsTimestamp(right);

  if (leftTimestamp === null && rightTimestamp === null) {
    return right;
  }

  if (leftTimestamp === null) {
    return right;
  }

  if (rightTimestamp === null) {
    return left;
  }

  return leftTimestamp >= rightTimestamp ? left : right;
}

/** True when `candidate` should replace `baseline` in client state (strictly newer timestamp). */
export function isProjectDetailsNewerThan(
  candidate: ProjectDetailsResponse | null | undefined,
  baseline: ProjectDetailsResponse | null | undefined
): boolean {
  if (!candidate?.project_id) {
    return false;
  }

  if (!baseline?.project_id || candidate.project_id !== baseline.project_id) {
    return true;
  }

  const candidateTimestamp = projectDetailsTimestamp(candidate);
  const baselineTimestamp = projectDetailsTimestamp(baseline);

  if (candidateTimestamp === null) {
    return false;
  }

  if (baselineTimestamp === null) {
    return true;
  }

  return candidateTimestamp > baselineTimestamp;
}

/**
 * For background merges (transaction-completion handlers, polling) that read-modify-write
 * against a `projectDetails` snapshot that can go stale while the write is in flight. Only
 * applies `candidate` to the store if it's not older than what's already there — protects
 * against a slower, stale-based write landing after a newer one and silently reverting it.
 *
 * Do NOT use this for a direct, user-initiated save of the entity currently being edited
 * (e.g. a panel saving its own current draft) — the user's own fresh edit should always win,
 * and gating it on a timestamp comparison could block a legitimate save.
 */
export function setProjectDetailsIfNewer(
  actionStore: {
    readonly projectDetails: ProjectDetailsResponse | null | undefined;
    readonly setProjectDetails: (next: ProjectDetailsResponse) => void;
  },
  candidate: ProjectDetailsResponse
): void {
  if (isProjectDetailsNewerThan(candidate, actionStore.projectDetails)) {
    actionStore.setProjectDetails(candidate);
  }
}

export function bumpProjectDetailsTimestamp(details: ProjectDetailsResponse): ProjectDetailsResponse {
  if (!details.project) {
    return details;
  }

  return {
    ...details,
    project: {
      ...details.project,
      timestamp: Date.now()
    }
  };
}

export const PROJECT_STORE_VERSIONS_KEY = "__project_store_versions";

/** Re-baseline `details` against `freshVersions`, without touching any actual content edit. */
export function withFreshProjectStoreVersions<T extends ProjectDetailsResponse | null>(details: T, freshVersions: unknown): T {
  if (!details?.project || freshVersions === undefined) {
    return details;
  }

  return {
    ...details,
    project: {
      ...details.project,
      [PROJECT_STORE_VERSIONS_KEY]: freshVersions
    }
  } as T;
}
